'use client';
import ProductNav from '@components/Product/Nav';
import Reviews from '@components/shared/Bazaarvoice/Reviews';
import { ProductProvider } from '@context/ProductProvider';
import Product from '@models/product';
import DynamicComponent from '@repo/ui/src/components/DynamicComponent';
import ErrorBoundary from '@repo/ui/src/components/ErrorBoundary';
import Seo from '@repo/ui/src/components/Seo';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { productViewed } from '@repo/ui/src/services/analytics';
import { productToAnalytics } from '@repo/ui/src/services/analytics/factories';
import { track } from '@repo/ui/src/services/analytics/utils';
import { MetaProps } from '@repo/ui/src/types/meta';
import { appComponentDirectory } from '@utils/appComponentDirectory';
import ApparelPage from '@views/ProductDetailPage/_ApparelPage';
import { PDPTemplate } from '@views/ProductDetailPage/_enums';
import * as React from 'react';
import ProductPage from './_ProductPage';

interface ComponentRowProps {
  props: { _meta: MetaProps };
}

const ProductDetailPage = (pageProps: any) => {
  const { locale } = useCurrentUrl();
  const product = React.useMemo(() => new Product(pageProps), [pageProps]);

  React.useEffect(() => {
    track(productViewed, productToAnalytics(product, locale));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const name = product?.shortTitleMain ?? product.title;
  const seo = Array.isArray(pageProps?.seo) ? pageProps?.seo[0] : pageProps.seo;

  return (
    <ProductProvider
      product={product}
      altProduct={pageProps.sizes?.[0]}
      addOns={pageProps.addOns}
      locale={locale}
    >
      <div>
        {!!seo && <Seo {...seo} jsonLD={{ type: 'product', product }} />}
        <ProductNav
          name={name}
          size={product?.shortTitlePrimary}
          hideCTA={
            pageProps.template === PDPTemplate.TierThree || product.ctaButtonType === 'noCTA'
          }
        />
        <DynamicComponent
          _componentTypes={{
            [PDPTemplate.PDPV2]: ProductPage,
            [PDPTemplate.PDPV2Light]: ProductPage,
            [PDPTemplate.NonGrillDark]: ProductPage,
            [PDPTemplate.NonGrillLight]: ProductPage,
            [PDPTemplate.TierOne]: ProductPage,
            [PDPTemplate.TierTwo]: ProductPage,
            [PDPTemplate.TierThree]: ProductPage,
            //todo: remove below grill option once gone from Amplience
            [PDPTemplate.Grill]: ProductPage,
            [PDPTemplate.Default]: ApparelPage,
          }}
          _field="template"
          _defaultComponent={ApparelPage}
          product={product}
          {...pageProps}
        />

        {pageProps.contentRows?.map((props: ComponentRowProps) => (
          <ErrorBoundary key={props?.props?._meta?.deliveryId}>
            <DynamicComponent
              _componentTypes={appComponentDirectory}
              zrStyle
              _parentProduct={pageProps.title}
              pairings={pageProps.productData?.pairings}
              {...props.props}
            />
          </ErrorBoundary>
        ))}
        <Reviews sku={product?.sku} type="product" />
      </div>
    </ProductProvider>
  );
};

export default ProductDetailPage;
